import { useState, useLayoutEffect, useMemo } from "react";
import {
  DEFAULT_COL_MIN_WIDTH,
  DEFAULT_COL_WIDTH,
  ACTIONS_COL_ID,
  BULK_SELECT_COL_ID
} from "components/common/EntityDataTable/Constants";
const calculateAssignableWidth = ({
  scrollContainerWidth,
  actionsColMinWidth,
  bulkSelectColWidth,
  columnIds,
  staticColumnWidths
}) => {
  const staticColWidths = columnIds.reduce((total, id) => {
    var _a;
    return total + ((_a = staticColumnWidths[id]) != null ? _a : 0);
  }, 0);
  return scrollContainerWidth - bulkSelectColWidth - actionsColMinWidth - staticColWidths;
};
const calculateColumnWidths = ({
  actionsColMinWidth,
  assignableWidth,
  attributeColumnIds,
  attributeColumnRenderers,
  bulkSelectColWidth,
  staticColumnWidths,
  headerMinWidths
}) => {
  const totalFlexColumns = attributeColumnIds.reduce((total, id) => {
    var _a;
    const { width = DEFAULT_COL_WIDTH } = (_a = attributeColumnRenderers[id]) != null ? _a : {};
    if (staticColumnWidths[id]) {
      return total;
    }
    return total + width;
  }, 0);
  const hasFlexColumns = totalFlexColumns > 0;
  const flexColWidth = hasFlexColumns ? assignableWidth / totalFlexColumns : 0;
  const actionsColWidth = !hasFlexColumns && assignableWidth > 0 ? assignableWidth + actionsColMinWidth : actionsColMinWidth;
  return {
    ...Object.fromEntries(
      attributeColumnIds.map((id) => {
        var _a, _b, _c;
        const { width = DEFAULT_COL_WIDTH, minWidth } = (_a = attributeColumnRenderers[id]) != null ? _a : {};
        const targetWidth = (_b = staticColumnWidths[id]) != null ? _b : Math.floor(flexColWidth * width);
        const baseMinWidth = minWidth != null ? minWidth : DEFAULT_COL_MIN_WIDTH;
        const resolvedMinWidth = Math.max(baseMinWidth, (_c = headerMinWidths[id]) != null ? _c : 0);
        return [id, !staticColumnWidths[id] && targetWidth < resolvedMinWidth ? resolvedMinWidth : targetWidth];
      })
    ),
    // When all data columns have static widths, the actions column acts as the elastic tail.
    // It fills any remaining space so the table spans the container even without row actions.
    [ACTIONS_COL_ID]: actionsColWidth,
    ...bulkSelectColWidth ? { [BULK_SELECT_COL_ID]: bulkSelectColWidth } : {}
  };
};
const calculateStaticColumnWidths = ({
  attributeColumnIds,
  attributeColumnRenderers,
  columnWidthPreferences,
  headerMinWidths
}) => attributeColumnIds.reduce((staticWidths, id) => {
  var _a, _b;
  const staticWidth = (_b = columnWidthPreferences == null ? void 0 : columnWidthPreferences[id]) != null ? _b : (_a = attributeColumnRenderers[id]) == null ? void 0 : _a.staticWidth;
  if (!staticWidth) {
    return staticWidths;
  }
  const resolvedStaticWidth = staticWidth === "matchHeader" || staticWidth < headerMinWidths[id] ? headerMinWidths[id] : staticWidth;
  return resolvedStaticWidth ? { ...staticWidths, [id]: resolvedStaticWidth } : staticWidths;
}, {});
const useColumnWidths = ({
  actionsColMinWidth,
  bulkSelectColWidth,
  columnRenderersByAttribute,
  columnIds,
  scrollContainerWidth,
  columnWidthPreferences,
  headerMinWidths
}) => {
  const [columnWidths, setColumnWidths] = useState({});
  const staticColumnWidths = useMemo(
    () => calculateStaticColumnWidths({
      attributeColumnIds: columnIds,
      attributeColumnRenderers: columnRenderersByAttribute,
      columnWidthPreferences,
      headerMinWidths
    }),
    [columnIds, columnRenderersByAttribute, columnWidthPreferences, headerMinWidths]
  );
  useLayoutEffect(() => {
    if (!scrollContainerWidth) {
      return;
    }
    const assignableWidth = calculateAssignableWidth({
      actionsColMinWidth,
      columnIds,
      bulkSelectColWidth,
      scrollContainerWidth,
      staticColumnWidths
    });
    setColumnWidths(
      calculateColumnWidths({
        actionsColMinWidth,
        assignableWidth,
        attributeColumnIds: columnIds,
        attributeColumnRenderers: columnRenderersByAttribute,
        bulkSelectColWidth,
        staticColumnWidths,
        headerMinWidths
      })
    );
  }, [
    actionsColMinWidth,
    bulkSelectColWidth,
    columnRenderersByAttribute,
    columnIds,
    scrollContainerWidth,
    columnWidthPreferences,
    staticColumnWidths,
    headerMinWidths
  ]);
  return columnWidths;
};
export default useColumnWidths;
